/**
 * Email validation
 * Validates email format using RFC 5322 compliant regex
 */
export function isEmail(email: string | null | undefined): boolean {
  if (!email) return false
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
  return emailRegex.test(email)
}

/**
 * Phone number validation
 * Supports international formats and Brazilian phone numbers
 */
export function isPhoneNumber(phone: string | null | undefined): boolean {
  if (!phone) return false
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '')
  // Brazilian phone: 10-11 digits (with area code)
  // International: 10-15 digits
  return digitsOnly.length >= 10 && digitsOnly.length <= 15
}

/**
 * Brazilian CPF validation
 * Validates CPF format and checksum
 */
export function isCPF(cpf: string | null | undefined): boolean {
  if (!cpf) return false

  // Remove non-digit characters
  const digits = cpf.replace(/\D/g, '')

  // CPF must have 11 digits
  if (digits.length !== 11) return false

  // Check for known invalid CPFs (all digits the same)
  if (/^(\d)\1{10}$/.test(digits)) return false

  // Validate first check digit
  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits.charAt(i)) * (10 - i)
  }
  let checkDigit = 11 - (sum % 11)
  if (checkDigit >= 10) checkDigit = 0
  if (checkDigit !== parseInt(digits.charAt(9))) return false

  // Validate second check digit
  sum = 0
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits.charAt(i)) * (11 - i)
  }
  checkDigit = 11 - (sum % 11)
  if (checkDigit >= 10) checkDigit = 0
  if (checkDigit !== parseInt(digits.charAt(10))) return false

  return true
}

/**
 * Brazilian CNPJ validation
 * Validates CNPJ format and checksum
 */
export function isCNPJ(cnpj: string | null | undefined): boolean {
  if (!cnpj) return false

  // Remove non-digit characters
  const digits = cnpj.replace(/\D/g, '')

  // CNPJ must have 14 digits
  if (digits.length !== 14) return false

  // Check for known invalid CNPJs (all digits the same)
  if (/^(\d)\1{13}$/.test(digits)) return false

  // Validate first check digit
  let sum = 0
  let pos = 5
  for (let i = 0; i < 12; i++) {
    sum += parseInt(digits.charAt(i)) * pos
    pos = pos === 2 ? 9 : pos - 1
  }
  let checkDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (checkDigit !== parseInt(digits.charAt(12))) return false

  // Validate second check digit
  sum = 0
  pos = 6
  for (let i = 0; i < 13; i++) {
    sum += parseInt(digits.charAt(i)) * pos
    pos = pos === 2 ? 9 : pos - 1
  }
  checkDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (checkDigit !== parseInt(digits.charAt(13))) return false

  return true
}

/**
 * Password validation
 * Validates password strength based on configurable requirements
 */
export interface PasswordRequirements {
  minLength?: number
  requireUppercase?: boolean
  requireLowercase?: boolean
  requireNumber?: boolean
  requireSpecialChar?: boolean
}

export function isValidPassword(
  password: string | null | undefined,
  requirements: PasswordRequirements = {}
): boolean {
  if (!password) return false

  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumber = true,
    requireSpecialChar = true
  } = requirements

  // Check minimum length
  if (password.length < minLength) return false

  // Check uppercase
  if (requireUppercase && !/[A-Z]/.test(password)) return false

  // Check lowercase
  if (requireLowercase && !/[a-z]/.test(password)) return false

  // Check number
  if (requireNumber && !/[0-9]/.test(password)) return false

  // Check special character
  if (requireSpecialChar && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) return false

  return true
}

/**
 * URL validation
 * Validates URL format
 */
export function isValidURL(url: string | null | undefined): boolean {
  if (!url) return false
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * Date validation
 * Validates date string is a valid date
 */
export function isValidDate(dateString: string | null | undefined): boolean {
  if (!dateString) return false
  const date = new Date(dateString)
  return !isNaN(date.getTime())
}

/**
 * Future date validation
 * Checks if date is in the future
 */
export function isFutureDate(dateString: string | null | undefined): boolean {
  if (!isValidDate(dateString)) return false
  const date = new Date(dateString!)
  const now = new Date()
  return date > now
}

/**
 * Past date validation
 * Checks if date is in the past
 */
export function isPastDate(dateString: string | null | undefined): boolean {
  if (!isValidDate(dateString)) return false
  const date = new Date(dateString!)
  const now = new Date()
  return date < now
}

/**
 * Numeric string validation
 * Checks if string contains only numbers
 */
export function isNumeric(value: string | null | undefined): boolean {
  if (!value) return false
  return /^\d+$/.test(value)
}

/**
 * Alpha-numeric validation
 * Checks if string contains only letters and numbers
 */
export function isAlphanumeric(value: string | null | undefined): boolean {
  if (!value) return false
  return /^[a-zA-Z0-9]+$/.test(value)
}

/**
 * Credit card validation (Luhn algorithm)
 * Validates credit card number using Luhn checksum
 */
export function isCreditCard(cardNumber: string | null | undefined): boolean {
  if (!cardNumber) return false

  // Remove spaces and hyphens
  const digits = cardNumber.replace(/[\s-]/g, '')

  // Must be all digits
  if (!/^\d+$/.test(digits)) return false

  // Must be 13-19 digits
  if (digits.length < 13 || digits.length > 19) return false

  // Luhn algorithm
  let sum = 0
  let isEven = false

  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits.charAt(i))

    if (isEven) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    isEven = !isEven
  }

  return sum % 10 === 0
}

/**
 * Range validation
 * Checks if number is within range
 */
export function isInRange(
  value: number | null | undefined,
  min: number,
  max: number
): boolean {
  if (value === null || value === undefined) return false
  return value >= min && value <= max
}

/**
 * String length validation
 * Checks if string length is within range
 */
export function hasValidLength(
  value: string | null | undefined,
  min?: number,
  max?: number
): boolean {
  if (!value) return false
  const length = value.length

  if (min !== undefined && length < min) return false
  if (max !== undefined && length > max) return false

  return true
}

/**
 * Pattern match validation
 * Checks if string matches regex pattern
 */
export function matchesPattern(
  value: string | null | undefined,
  pattern: RegExp
): boolean {
  if (!value) return false
  return pattern.test(value)
}

/**
 * Required field validation
 * Checks if value is not empty/null/undefined
 */
export function isRequired(value: any): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string' && value.trim() === '') return false
  if (Array.isArray(value) && value.length === 0) return false
  return true
}
